// sectionSchema.ts
// ─────────────────────────────────────────────────────────────────────────────
// Defines which fields to render for each section of each content type.
// Rules:
//   • field.key maps 1-to-1 to the JSON key in the API response.
//   • The UI always shows "Title" for the title field.
//   • The API may use either "title" or "headline" — both are handled transparently.
//   • withToggle: true        → renders a Toggle next to the field label
//   • asMainContentTitle: true → display current value wrapped in "[ value ]"
//   • alwaysShowHeading: true  → show section heading even if no `label` in API
// ─────────────────────────────────────────────────────────────────────────────

export interface IconSubItem {
  id:      number;
  Icon:    string;
  label:   string;
  iconAlt: string;
}

export interface ListSubField {
  key:          string;
  label:        string;
  type:         'text' | 'textarea' | 'image';
  required?:    boolean;
  placeholder?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Discriminated union — FieldSchema
// ─────────────────────────────────────────────────────────────────────────────

interface FieldSchemaBase {
  key:                 string;
  label:               string;
  required?:           boolean;
  withToggle?:         boolean;  // show Toggle next to label
  asMainContentTitle?: boolean;  // wrap value as [ value ]
  placeholder?:        string;
}

export interface TextField extends FieldSchemaBase {
  type: 'text';
}

export interface TextareaField extends FieldSchemaBase {
  type: 'textarea';
}

export interface ImageField extends FieldSchemaBase {
  type: 'image';
}

export interface SelectFieldSchema extends FieldSchemaBase {
  type:    'select';
  options: { value: string; label: string }[];
}

export interface ListFieldSchema extends FieldSchemaBase {
  type:       'list';
  listFields: ListSubField[];
}

export type FieldSchema =
  | TextField
  | TextareaField
  | ImageField
  | SelectFieldSchema
  | ListFieldSchema;

export type FieldType = FieldSchema['type'];

export interface SectionSchema {
  label:              string;   // used in "{pageLabel}-{label} sector" heading
  alwaysShowHeading?: boolean;  // show heading even when API section has no `label`
  fields:             FieldSchema[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable field definitions — typed as specific variants (not widened union)
// ─────────────────────────────────────────────────────────────────────────────

const TITLE_FIELD: TextField = {
  key: 'title', label: 'Title', type: 'text',
  required: true, placeholder: 'Navigation name',
};

const TITLE_FIELD_WITH_TOGGLE: TextField = {
  key: 'title', label: 'Main title', type: 'text',
  withToggle: true, placeholder: 'Navigation name',
};

const DESCRIPTION_FIELD: TextareaField = {
  key: 'description', label: 'Sub title/Description', type: 'textarea',
  placeholder: 'Title description',
};

const DESCRIPTION_FIELD_WITH_TOGGLE: TextareaField = {
  key: 'description', label: 'Sub title/Description', type: 'textarea',
  withToggle: true, placeholder: 'Title description',
};

// "label" key in API → displayed as "Main content title" with [ value ] format
const LABEL_AS_MAIN_CONTENT: TextField = {
  key: 'label', label: 'Main content title', type: 'text',
  required: true, asMainContentTitle: true, placeholder: 'Navigation name',
};

const PRIMARY_BTN_LABEL: TextField = {
  key: 'primaryButton.label', label: 'Secondary button', type: 'text',
  placeholder: 'Text button',
};

// ─────────────────────────────────────────────────────────────────────────────
// List sub-field definitions
// ─────────────────────────────────────────────────────────────────────────────

// Product section cards: title + description + image + services (items[])
const PRODUCT_LIST_SUBFIELDS: ListSubField[] = [
  { key: 'title',       label: 'Title',                 type: 'text',     required: true, placeholder: 'Navigation name' },
  { key: 'description', label: 'Sub title/Description', type: 'textarea', placeholder: 'Title description' },
  { key: 'img',         label: 'Company logo',          type: 'image' },
];

// Contact section cards: title + description + button label + icon
const CONTACT_LIST_SUBFIELDS: ListSubField[] = [
  { key: 'title',       label: 'Title',       type: 'text',     required: true, placeholder: 'Navigation name' },
  { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Card description' },
  { key: 'buttonLabel', label: 'Button',      type: 'text',     placeholder: 'Navigation name' },
  { key: 'imgIco',      label: 'Icons',       type: 'image' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Schema registry — keyed by `${apiSlug}__${sectionKey}`
// ─────────────────────────────────────────────────────────────────────────────

type SchemaRegistry = Record<string, SectionSchema>;

const SCHEMAS: SchemaRegistry = {

  // ── product ──────────────────────────────────────────────────────────────────

  // heroSection (Image 1): Title + Secondary button, no section label in API
  'product__heroSection': {
    label: 'Hero',
    alwaysShowHeading: true,
    fields: [
      TITLE_FIELD,
      PRIMARY_BTN_LABEL,
    ],
  },

  // productSection (Images 2–5):
  //   Main content title (label), Title *, Sub title/Description,
  //   Card/Product list → title, description, image, services
  'product__productSection': {
    label: 'Product',
    alwaysShowHeading: true,
    fields: [
      LABEL_AS_MAIN_CONTENT,
      TITLE_FIELD,
      DESCRIPTION_FIELD,
      {
        key: 'lists', label: 'Card / Product', type: 'list',
        listFields: PRODUCT_LIST_SUBFIELDS,
      },
    ],
  },

  // contactSection (Image 6):
  //   Main content title (label), Main title (toggle), Sub title/Description (toggle),
  //   Card/Contact us list → title, description, button, icon
  'product__contactSection': {
    label: 'Contact',
    alwaysShowHeading: true,
    fields: [
      LABEL_AS_MAIN_CONTENT,
      TITLE_FIELD_WITH_TOGGLE,
      DESCRIPTION_FIELD_WITH_TOGGLE,
      {
        key: 'lists', label: 'Card / Contact us', type: 'list',
        listFields: CONTACT_LIST_SUBFIELDS,
      },
    ],
  },

  // ── company ──────────────────────────────────────────────────────────────────

  'company__heroSection': {
    label: 'Hero',
    alwaysShowHeading: true,
    fields: [TITLE_FIELD, DESCRIPTION_FIELD, PRIMARY_BTN_LABEL],
  },
  'company__serviceSection': {
    label: 'Service',
    fields: [TITLE_FIELD, DESCRIPTION_FIELD],
  },
  'company__contactSection': {
    label: 'Contact',
    fields: [
      LABEL_AS_MAIN_CONTENT,
      TITLE_FIELD_WITH_TOGGLE,
      DESCRIPTION_FIELD_WITH_TOGGLE,
      { key: 'lists', label: 'Card / Contact us', type: 'list', listFields: CONTACT_LIST_SUBFIELDS },
    ],
  },

  // ── career ───────────────────────────────────────────────────────────────────

  'career__heroSection': {
    label: 'Hero',
    alwaysShowHeading: true,
    fields: [TITLE_FIELD, DESCRIPTION_FIELD, PRIMARY_BTN_LABEL],
  },
  'career__joinSection': {
    label: 'Join Us',
    fields: [TITLE_FIELD, DESCRIPTION_FIELD],
  },
  'career__principleSection': {
    label: 'Principle',
    fields: [
      TITLE_FIELD,
      {
        key: 'lists', label: 'Principles', type: 'list',
        listFields: [
          { key: 'title',       label: 'Title',       type: 'text',     required: true },
          { key: 'description', label: 'Description', type: 'textarea' },
        ],
      },
    ],
  },

  // ── contact ──────────────────────────────────────────────────────────────────

  'contact__contactSection': {
    label: 'Contact',
    fields: [
      LABEL_AS_MAIN_CONTENT,
      TITLE_FIELD_WITH_TOGGLE,
      DESCRIPTION_FIELD_WITH_TOGGLE,
      { key: 'lists', label: 'Card / Contact us', type: 'list', listFields: CONTACT_LIST_SUBFIELDS },
    ],
  },

  // ── sale / business ──────────────────────────────────────────────────────────

  'saleContact__formSection': {
    label: 'Form',
    fields: [TITLE_FIELD, DESCRIPTION_FIELD],
  },
  'businessContact__formSection': {
    label: 'Form',
    fields: [TITLE_FIELD, DESCRIPTION_FIELD],
  },

  // ── job-detail ───────────────────────────────────────────────────────────────

  'jobDetail__heroSection': {
    label: 'Hero',
    alwaysShowHeading: true,
    fields: [TITLE_FIELD, DESCRIPTION_FIELD],
  },
  'jobDetail__descriptionSection': {
    label: 'Description',
    fields: [TITLE_FIELD, DESCRIPTION_FIELD],
  },

  // ── job-position ─────────────────────────────────────────────────────────────

  'jobPosition__listSection': {
    label: 'Position List',
    fields: [
      TITLE_FIELD,
      {
        key: 'lists', label: 'Positions', type: 'list',
        listFields: [
          { key: 'title',       label: 'Title',       type: 'text',     required: true },
          { key: 'description', label: 'Description', type: 'textarea' },
        ],
      },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export const getSectionSchema = (apiSlug: string, sectionKey: string): SectionSchema | null =>
  SCHEMAS[`${apiSlug}__${sectionKey}`] ?? null;

// Keys that are interchangeable in the API
const TITLE_ALIASES = ['title', 'headline'] as const;

/**
 * Read a (possibly nested) value using dot-notation.
 * If the final key is 'title' or 'headline', tries both aliases.
 */
export const getByKey = (obj: Record<string, unknown>, key: string): unknown => {
  return key.split('.').reduce<unknown>((acc, k, idx, parts) => {
    if (acc === null || typeof acc !== 'object') return undefined;
    const record = acc as Record<string, unknown>;
    if (idx === parts.length - 1 && (TITLE_ALIASES as readonly string[]).includes(k)) {
      for (const alias of TITLE_ALIASES) {
        if (alias in record) return record[alias];
      }
      return undefined;
    }
    return record[k];
  }, obj);
};

/**
 * Return a new object with the (possibly nested) key set to value.
 * If the final key is a title alias, writes to whichever alias already exists.
 */
export const setByKey = (
  obj:   Record<string, unknown>,
  key:   string,
  value: unknown,
): Record<string, unknown> => {
  const parts = key.split('.');
  if (parts.length === 1) {
    if ((TITLE_ALIASES as readonly string[]).includes(key)) {
      const existing = TITLE_ALIASES.find(a => a in obj) ?? key;
      return { ...obj, [existing]: value };
    }
    return { ...obj, [key]: value };
  }
  const [head, ...rest] = parts;
  const nested = (obj[head] !== null && typeof obj[head] === 'object')
    ? (obj[head] as Record<string, unknown>)
    : {};
  return { ...obj, [head]: setByKey(nested, rest.join('.'), value) };
};